import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import type { Hold } from '../../../types/climb';
import type { ClimbScan, PhotoArea } from '../../../types/climb-scan';
import { WAND_TOLERANCE_DEFAULT, loadWallPixels, wandOutline, type WallPixels } from './colour-wand';
import { maybeIndices, minScoreFloor, shownIndices, wallPlaceholders } from './detected-holds';
import type { ScanAreaControl, ScanConfidence, ScanExclusion, ScanWand } from './ScanWallTools';

/** How often an area being looked at again is read back. */
const AREA_POLL_INTERVAL_MS = 5000;

const NO_HOLDS: Hold[] = [];

/**
 * Where the wall's holds are written: the gym's walls
 * (`/climb_scans/:id/...`) or a board's photo (`/boards/:id/hold_scan/...`).
 * The rules are the same on both (the server's `EditsScannedWall`), only the
 * address differs.
 */
export interface ScanWallApi {
  update: (scanId: string, attrs: { min_score?: number | null; excluded_holds?: number[] }) => Promise<ClimbScan>;
  addHold: (scanId: string, outline: Array<[number, number]>) => Promise<ClimbScan>;
  scanArea: (scanId: string, area: PhotoArea) => Promise<ClimbScan>;
  /** Reads the scan again and puts it in the page's state; the answer, or null. */
  reload: (scanId: string) => Promise<ClimbScan | null>;
}

interface UseScanWallToolsOptions {
  scan: ClimbScan | null;
  setScan: Dispatch<SetStateAction<ClimbScan | null>>;
  api: ScanWallApi;
  /** The photo's size: placeholders are drawn against it. */
  naturalSize: { width: number; height: number } | null;
  /**
   * Puts every tool down when it changes — another scan opened, a climb opened
   * on it — so a press on the photo is never read as one of them.
   */
  resetKey?: unknown;
  /** Told when an area looked at again is answered, with the wall as it is now. */
  onAreaSettled?: (scan: ClimbScan) => void;
}

/**
 * **What a setter does to the holds a scan found**, wherever the wall is: the
 * scans page's walls and a board's photo (docs/SPRAY_WALL_SCAN_PLAN.md). The
 * confidence slider, excluding holds by hand, the colour-wand that adds one,
 * and looking again at an area — each saved on the spot.
 *
 * Answers the props of `ScanWallTools` (the controls) and of
 * `ScanPlaceholderPicker` (the photo), which is where the gestures land.
 */
export function useScanWallTools({ scan, setScan, api, naturalSize, resetKey, onAreaSettled }: UseScanWallToolsOptions) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const apiRef = useRef(api);
  apiRef.current = api;
  const notifyRef = useRef({ t, toast, onAreaSettled });
  notifyRef.current = { t, toast, onAreaSettled };

  /**
   * **The confidence slider**, live while it is dragged and saved on the
   * scan when it is let go (`min_score`), so the wall stays as the setter left
   * it for the next setter and for the member app's editor. Keyed by scan, so
   * a drag on one wall is not carried to the next one opened.
   */
  const [minScoreDraft, setMinScoreDraft] = useState<{ scanId: string; value: number | null } | null>(null);
  const [savingMinScore, setSavingMinScore] = useState(false);
  const minScore =
    minScoreDraft && minScoreDraft.scanId === scan?.id ? minScoreDraft.value : scan?.min_score ?? null;

  /** Where in `detected_holds` each shown hold — each placeholder — is. */
  const shownAt = useMemo(
    () => (scan ? shownIndices(scan.detected_holds, scan.sensitivity, minScore, scan.excluded_holds ?? []) : []),
    [scan, minScore]
  );
  const shown = useMemo(() => (scan ? shownAt.map((index) => scan.detected_holds[index]) : []), [scan, shownAt]);
  const excludedCount = scan?.excluded_holds?.length ?? 0;

  /**
   * Every hold the scan found, as one pool of placeholders. Plain holds, all of
   * them — a wall has no start and no top (see `wallPlaceholders`). The ones
   * the detector was unsure of are among them, drawn as maybes, unless the wall
   * was read at "fewer" (`shownDetections`).
   */
  const placeholders = useMemo(
    () => (scan && naturalSize ? wallPlaceholders(shown, naturalSize.width, naturalSize.height) : NO_HOLDS),
    [scan, shown, naturalSize]
  );
  /** Which placeholders are maybes: drawn fainter and dotted until taken. */
  const maybe = useMemo(() => maybeIndices(shown), [shown]);

  /** The placeholders lit on the wall, by place — what "exclude" takes off it. */
  const [chosen, setChosen] = useState<number[]>([]);
  const [savingExcluded, setSavingExcluded] = useState(false);

  /**
   * **Excluding and putting back are the same write**: the whole list, shown
   * at once (the holds leave the photo as the button is pressed) and put back
   * as it was if the server says no.
   */
  async function saveExcluded(next: number[]) {
    if (!scan) return;
    const scanId = scan.id;
    const before = scan.excluded_holds ?? [];
    const patchExcluded = (list: number[]) =>
      setScan((prev) => (prev?.id === scanId ? { ...prev, excluded_holds: list } : prev));

    patchExcluded(next);
    setSavingExcluded(true);
    try {
      const updated = await api.update(scanId, { excluded_holds: next });
      patchExcluded(updated.excluded_holds ?? next);
    } catch {
      patchExcluded(before);
      toast({ title: t('gymAdmin.scans.exclude.error'), variant: 'destructive' });
    } finally {
      setSavingExcluded(false);
    }
  }

  /**
   * **The excluded holds, shown on the wall**: the count is a switch, and while
   * it is on the photo shows the holds excluded by hand instead of the wall's
   * — each one pressable, and a press asks whether to put it back. The picker
   * is remounted (`pickerKey`) on every answer and every switch, so nothing
   * stays lit from the other pool.
   */
  const [showingExcluded, setShowingExcludedState] = useState(false);
  const [restoreAt, setRestoreAt] = useState<number | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const excludedAt = useMemo(() => scan?.excluded_holds ?? [], [scan]);
  const excludedPlaceholders = useMemo(
    () =>
      scan && naturalSize
        ? wallPlaceholders(
            excludedAt.map((index) => scan.detected_holds[index]).filter(Boolean),
            naturalSize.width,
            naturalSize.height
          )
        : NO_HOLDS,
    [scan, excludedAt, naturalSize]
  );
  const showing = showingExcluded && excludedAt.length > 0;

  function setShowingExcluded(next: boolean) {
    setShowingExcludedState(next);
    setChosen([]);
    setRestoreAt(null);
    setPickerKey((key) => key + 1);
    if (next) {
      setWandActiveState(false);
      setDrawingAreaState(false);
    }
  }

  /** The excluded hold pressed, by its place in the excluded pool. */
  function askToRestore(places: number[]) {
    const place = places.at(-1);
    if (place !== undefined && excludedAt[place] !== undefined) setRestoreAt(excludedAt[place]);
  }

  function answerRestore(restore: boolean) {
    const index = restoreAt;
    setRestoreAt(null);
    setPickerKey((key) => key + 1);
    if (!restore || index === null) return;

    const rest = excludedAt.filter((other) => other !== index);
    void saveExcluded(rest);
    if (rest.length === 0) setShowingExcludedState(false);
  }

  function excludeChosen() {
    const chosenAt = chosen.map((place) => shownAt[place]).filter((index) => index !== undefined);
    if (chosenAt.length === 0) return;

    void saveExcluded([...new Set([...(scan?.excluded_holds ?? []), ...chosenAt])].sort((a, b) => a - b));
  }

  const confidenceFloor = useMemo(() => (scan ? minScoreFloor(scan.detected_holds) : null), [scan]);

  async function saveMinScore(value: number | null) {
    if (!scan) return;
    const scanId = scan.id;
    setMinScoreDraft({ scanId, value });
    setSavingMinScore(true);
    try {
      const updated = await api.update(scanId, { min_score: value });
      setScan((prev) => (prev?.id === scanId ? { ...prev, min_score: updated.min_score ?? null } : prev));
    } catch {
      toast({ title: t('gymAdmin.scans.saveError'), variant: 'destructive' });
    } finally {
      setMinScoreDraft(null);
      setSavingMinScore(false);
    }
  }

  /**
   * "Draw round the missing holds", armed from the controls and drawn on the
   * photo.
   */
  const [drawingArea, setDrawingAreaState] = useState(false);

  /**
   * **The colour-wand** — a hold added by hand: armed from the controls, a
   * press on the photo is a point, and the patch of its colour is grown in the
   * browser (`colour-wand.ts`) so the tolerance slider follows the hand. The
   * photo's pixels are read once per photo, on first arming.
   */
  const [wandActive, setWandActiveState] = useState(false);
  const [wandPoint, setWandPoint] = useState<{ x: number; y: number } | null>(null);
  const [wandTolerance, setWandTolerance] = useState(WAND_TOLERANCE_DEFAULT);
  const [wandPixels, setWandPixels] = useState<{ url: string; pixels: WallPixels } | null>(null);
  const [wandFailed, setWandFailed] = useState(false);
  const [addingHold, setAddingHold] = useState(false);
  const wandLoading = useRef<string | null>(null);

  // One way of pressing the photo at a time.
  function setDrawingArea(drawing: boolean) {
    setDrawingAreaState(drawing);
    if (drawing) {
      setWandActiveState(false);
      setShowingExcludedState(false);
    }
  }
  function setWandActive(active: boolean) {
    setWandActiveState(active);
    setWandPoint(null);
    if (active) {
      setDrawingAreaState(false);
      setShowingExcludedState(false);
    }
  }

  useEffect(() => {
    setDrawingAreaState(false);
    setWandActiveState(false);
    setWandPoint(null);
    setShowingExcludedState(false);
    setRestoreAt(null);
  }, [scan?.id, resetKey]);

  const photoForWand = scan?.image_url ?? null;
  useEffect(() => {
    if (!wandActive || !photoForWand || wandLoading.current === photoForWand) return;

    wandLoading.current = photoForWand;
    setWandFailed(false);
    loadWallPixels(photoForWand)
      .then((pixels) => {
        if (wandLoading.current === photoForWand) setWandPixels({ url: photoForWand, pixels });
      })
      .catch(() => {
        if (wandLoading.current !== photoForWand) return;
        wandLoading.current = null;
        setWandFailed(true);
      });
  }, [wandActive, photoForWand]);

  const wandReady = wandPixels && wandPixels.url === photoForWand ? wandPixels.pixels : null;
  const wandResult = useMemo(
    () => (wandReady && wandPoint ? wandOutline(wandReady, wandPoint, wandTolerance) : null),
    [wandReady, wandPoint, wandTolerance]
  );

  const wandStatus: ScanWand['status'] = wandFailed
    ? 'failed'
    : !wandReady
      ? 'loading'
      : !wandResult
        ? 'pick'
        : wandResult.overflow
          ? 'overflow'
          : wandResult.tooSmall
            ? 'tooSmall'
            : 'ready';

  async function addWandHold() {
    if (!scan || !wandResult?.outline) return;
    const scanId = scan.id;
    const outline = wandResult.outline.map(
      ([x, y]): [number, number] => [Math.round(x * 100000) / 100, Math.round(y * 100000) / 100]
    );
    setAddingHold(true);
    try {
      const updated = await api.addHold(scanId, outline);
      setScan((prev) => (prev?.id === scanId ? updated : prev));
      setWandPoint(null);
      toast({ title: t('gymAdmin.scans.wand.added') });
    } catch {
      toast({ title: t('gymAdmin.scans.wand.error'), variant: 'destructive' });
    } finally {
      setAddingHold(false);
    }
  }

  // "Scan this area" is asked of the model that answered the wall, where the
  // gym has one that traces outlines.
  const areaScannable = !!(scan?.area_scannable ?? scan?.detector === 'sam');

  async function scanArea(area: PhotoArea) {
    if (!scan) return;

    try {
      const updated = await api.scanArea(scan.id, area);
      setScan(updated);
      // Answered without the request on it waiting means nothing will come
      // of it, and a "looking" that never ends is worse than saying so.
      if (!updated.area_scan?.running) throw new Error(updated.area_scan?.error);
      toast({ title: t('gymAdmin.scans.areaScan.queued') });
    } catch (error) {
      toast({
        title: t('gymAdmin.scans.areaScan.error'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  // **An area being looked at again**, for holds the scan missed. The wall
  // stays as it is meanwhile — the area only ever adds holds, at the end of
  // the pool — so the setter goes on working and the new placeholders simply
  // appear, with a word on how many.
  const areaPollingScanId = scan?.area_scan?.running ? scan.id : null;

  useEffect(() => {
    if (!areaPollingScanId) return;

    const timer = setInterval(async () => {
      const data = await apiRef.current.reload(areaPollingScanId);
      const outcome = data?.area_scan;
      if (!data || !outcome || outcome.running) return;

      const { t: translate, toast: notify, onAreaSettled: settled } = notifyRef.current;
      settled?.(data);
      if (outcome.status === 'failed') {
        notify({ title: translate('gymAdmin.scans.areaScan.failed'), variant: 'destructive' });
      } else if (outcome.added) {
        notify({ title: translate('gymAdmin.scans.areaScan.done', { count: outcome.added }) });
      } else {
        notify({ title: translate('gymAdmin.scans.areaScan.none') });
      }
    }, AREA_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [areaPollingScanId]);

  const confidence: ScanConfidence | undefined =
    scan && confidenceFloor != null
      ? {
          value: minScore,
          floor: confidenceFloor,
          // Under the line only: the hand-excluded are counted apart.
          hidden: scan.detected_holds.length - excludedCount - shown.length,
          total: scan.detected_holds.length,
          saving: savingMinScore,
          onChange: (value) => setMinScoreDraft({ scanId: scan.id, value }),
          onCommit: saveMinScore,
        }
      : undefined;

  const exclusion: ScanExclusion = {
    chosen: showing ? 0 : chosen.length,
    excluded: excludedCount,
    saving: savingExcluded,
    onExclude: excludeChosen,
    onRestore: () => {
      setShowingExcludedState(false);
      void saveExcluded([]);
    },
    showing,
    onShowingChange: setShowingExcluded,
    restoring: restoreAt !== null,
    onAnswerRestore: answerRestore,
  };

  const wand: ScanWand = {
    active: wandActive,
    onActiveChange: setWandActive,
    tolerance: wandTolerance,
    onToleranceChange: setWandTolerance,
    status: wandStatus,
    adding: addingHold,
    onAdd: () => void addWandHold(),
  };

  const areaControl: ScanAreaControl | undefined =
    areaScannable && scan
      ? { running: Boolean(scan.area_scan?.running), drawing: drawingArea, onDrawingChange: setDrawingArea }
      : undefined;

  return {
    minScore,
    shownAt,
    shown,
    /** The holds lit on the photo, as places in `detected_holds`. */
    chosenAt: chosen.map((place) => shownAt[place]).filter((index): index is number => index !== undefined),
    placeholders,
    maybe,
    /** The props of `ScanWallTools`; null on a wall with no holds to act on. */
    tools: scan && scan.detected_holds.length > 0 ? { confidence, exclusion, wand, areaScan: areaControl } : null,
    /** What `ScanPlaceholderPicker` needs for the same gestures on the photo. */
    picker: {
      /** Remount the picker on this, so nothing stays lit across pools. */
      key: `${showing ? 'excluded' : 'wall'}-${pickerKey}`,
      /** The holds drawn: the wall's, or the excluded ones while they are shown. */
      placeholders: showing ? excludedPlaceholders : placeholders,
      maybe: showing ? [] : maybe,
      onChosenChange: showing ? askToRestore : setChosen,
      wand: wandActive
        ? {
            outline: wandResult?.outline ?? null,
            point: wandPoint,
            onPick: setWandPoint,
            onCancel: () => setWandActive(false),
          }
        : undefined,
      areaScan:
        areaScannable && scan
          ? {
              running: Boolean(scan.area_scan?.running),
              area: scan.area_scan?.area,
              drawing: drawingArea,
              onDrawingChange: setDrawingArea,
              onScan: (area: PhotoArea) => void scanArea(area),
            }
          : undefined,
    },
  };
}
